import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { useGlobalData } from '../../context/GlobalDataContext';
import companiesData from '../../data/companies.json';
import './MNAVLineChart.css';

const MNAVLineChart = ({ assetType, title = "MNAV Over Time" }) => {
  const { getTickerTimeSeries } = useGlobalData();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Detect light mode and update when it changes
  const [isLightMode, setIsLightMode] = useState(() => {
    return document.body.classList.contains('light-mode') || 
           document.querySelector('.landing-page')?.classList.contains('light-mode');
  });
  
  useEffect(() => {
    const checkLightMode = () => {
      setIsLightMode(
        document.body.classList.contains('light-mode') || 
        document.querySelector('.landing-page')?.classList.contains('light-mode')
      );
    };
    
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Check immediately
    checkLightMode();
    
    // Set up observer to watch for class changes
    const observer = new MutationObserver(checkLightMode);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    const landingPage = document.querySelector('.landing-page');
    if (landingPage) {
      observer.observe(landingPage, { attributes: true, attributeFilter: ['class'] });
    }

    // Listen for window resize
    window.addEventListener('resize', handleResize);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Get companies for this asset type (only tracked companies)
  const companies = (companiesData[assetType] || []).filter(c => c.trackData === true);
  
  // Get color palette based on asset type
  const getColorPalette = () => {
    switch (assetType) {
      case 'bitcoin':
        return [
          '#ffff00', // Yellow
          '#ffeb3b', // Light yellow
          '#ffc107', // Amber
          '#ff9800', // Orange
          '#ff4500', // Red-orange
          '#f44336', // Red
          '#dc143c', // Crimson red
          '#ff1493', // Deep pink
          '#ff69b4', // Hot pink
          '#32cd32', // Lime green
          '#00ff7f', // Spring green
          '#00ff00', // Green
          '#228b22', // Forest green
          '#006400', // Dark green
          '#00ced1', // Dark turquoise
          '#40e0d0'  // Turquoise
        ];
      case 'ethereum':
        return [
          '#add8e6',   // Light blue
          '#87ceeb',   // Sky blue
          '#6495ed',   // Cornflower blue
          '#4682b4',   // Steel blue
          '#4169e1',   // Royal blue
          '#627eea',   // Ethereum blue
          '#483d8b',   // Dark slate blue
          '#4b0082',   // Indigo
          '#8a2be2',   // Blue violet
          '#800080'    // Purple
        ];
      case 'solana':
        return [
          '#32cd32',   // Lime green (bright green)
          '#2ecc71',   // Emerald green
          '#00c9a7',   // Turquoise
          '#48d1cc',   // Medium turquoise
          '#1e90ff',   // Dodger blue
          '#4682b4',   // Steel blue
          '#8a2be2',   // Blue violet
          '#800080'    // Purple
        ];
      default:
        return ['#6b7280'];
    }
  };

  const colorPalette = getColorPalette();

  // Simple interpolation function for MNAV data
  const interpolateMNAVData = (data) => {
    if (!data || data.length < 2) return data;
    
    const interpolated = [...data];
    
    // First pass: interpolate zero values
    for (let i = 0; i < interpolated.length; i++) {
      if (interpolated[i].mnav === 0 || interpolated[i].mnav === null) {
        // Find previous non-zero value
        let prevValue = null;
        let prevIndex = -1;
        for (let j = i - 1; j >= 0; j--) {
          if (interpolated[j].mnav > 0) {
            prevValue = interpolated[j].mnav;
            prevIndex = j;
            break;
          }
        }
        
        // Find next non-zero value
        let nextValue = null;
        let nextIndex = -1;
        for (let j = i + 1; j < interpolated.length; j++) {
          if (interpolated[j].mnav > 0) {
            nextValue = interpolated[j].mnav;
            nextIndex = j;
            break;
          }
        }
        
        // Interpolate if we have both values
        if (prevValue !== null && nextValue !== null) {
          const prevDate = new Date(interpolated[prevIndex].date);
          const nextDate = new Date(interpolated[nextIndex].date);
          const currentDate = new Date(interpolated[i].date);
          
          const totalDays = (nextDate - prevDate) / (1000 * 60 * 60 * 24);
          const currentDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
          const ratio = totalDays > 0 ? currentDays / totalDays : 0;
          
          interpolated[i].mnav = parseFloat((prevValue + (nextValue - prevValue) * ratio).toFixed(2));
        }
      }
    }
    
    // Second pass: fill weekend gaps
    const finalData = [];
    for (let i = 0; i < interpolated.length - 1; i++) {
      const currentDate = new Date(interpolated[i].date);
      const nextDate = new Date(interpolated[i + 1].date);
      const daysDiff = Math.floor((nextDate - currentDate) / (1000 * 60 * 60 * 24));
      
      finalData.push(interpolated[i]);
      
      // If there are missing days (weekends), interpolate
      if (daysDiff > 1) {
        const currentMNAV = interpolated[i].mnav;
        const nextMNAV = interpolated[i + 1].mnav;
        
        for (let day = 1; day < daysDiff; day++) {
          const interpolatedDate = new Date(currentDate);
          interpolatedDate.setDate(currentDate.getDate() + day);
          
          const ratio = day / daysDiff;
          const interpolatedMNAV = parseFloat((currentMNAV + (nextMNAV - currentMNAV) * ratio).toFixed(2));
          
          finalData.push({
            ...interpolated[i],
            date: interpolatedDate.toISOString().split('T')[0],
            mnav: interpolatedMNAV
          });
        }
      }
    }
    
    // Add the last data point
    finalData.push(interpolated[interpolated.length - 1]);
    
    return finalData;
  };

  // Prepare chart data for each ticker
  const plotData = companies.map(company => {
    const tickerData = getTickerTimeSeries(company.ticker);
    
    if (!tickerData || tickerData.length === 0) {
      return null;
    }

    // Sort data by date
    const sortedData = tickerData.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Filter to dates after June 15, 2025 for Bitcoin asset type
    const filteredData = (() => {
      if (assetType === 'bitcoin') {
        const cutoff = new Date('2025-06-15');
        return sortedData.filter(row => {
          const rowDate = row.date ? new Date(row.date) : null;
          return rowDate && rowDate >= cutoff;
        });
      }
      return sortedData;
    })();

    if (!filteredData.length) {
      return null;
    }
    
    // Always use 'mnav' field - don't filter out if it doesn't exist or is null
    const mnavField = 'mnav';
    
    // Apply interpolation to smooth weekend and zero values
    const interpolatedData = interpolateMNAVData(filteredData);
    
    // Get color by index, cycling through the palette
    const colorIndex = companies.indexOf(company);
    const color = colorPalette[colorIndex % colorPalette.length];
    
    return {
      x: interpolatedData.map(d => d.date),
      y: interpolatedData.map(d => d[mnavField] || 0),
      type: 'scatter',
      mode: 'lines',
      name: company.ticker,
      line: {
        color: color,
        width: 2,
        dash: 'solid'
      },
      hovertemplate: `<b>${company.ticker}</b><br>Date: %{x}<br>MNAV: %{y}<extra></extra>`
    };
  }).filter(Boolean); // Remove null entries

  // Sort plotData alphabetically by ticker name for consistent legend ordering
  plotData.sort((a, b) => {
    const nameA = a.name || '';
    const nameB = b.name || '';
    return nameA.localeCompare(nameB);
  });

  const isMobile = windowWidth <= 768;
  const width = windowWidth;
  
  // Responsive title size
  let titleSize = 24;
  if (width <= 480) {
    titleSize = 12;
  } else if (width <= 768) {
    titleSize = 14;
  } else if (width <= 1024) {
    titleSize = 18;
  }
  
  const layout = {
    title: {
      text: title,
      font: { 
        size: titleSize, 
        family: "'TWK Everett', sans-serif",
        color: isLightMode ? '#000000' : '#ffffff'
      },
      x: 0.02,
      y: 0.95,
      xanchor: 'left',
      yanchor: 'top'
    },
    xaxis: {
      title: '',
      showgrid: true,
      gridcolor: isLightMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
      color: isLightMode ? '#000000' : '#ffffff'
    },
    yaxis: {
      title: '',
      showgrid: true,
      gridcolor: isLightMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
      color: isLightMode ? '#000000' : '#ffffff'
    },
    plot_bgcolor: 'transparent',
    paper_bgcolor: 'transparent',
    font: {
      family: "'TWK Everett', sans-serif",
      color: isLightMode ? '#000000' : '#ffffff'
    },
    margin: { 
      l: 80, 
      r: isMobile ? 40 : 180, 
      t: 80, 
      b: isMobile ? (plotData.length > 6 ? 140 : 120) : 40 
    },
    height: 400,
    showlegend: true,
    legend: isMobile ? {
      orientation: 'h',
      x: 0.5,
      y: -0.6,
      xanchor: 'center',
      yanchor: 'top',
      bgcolor: 'rgba(0, 0, 0, 0)',
      bordercolor: 'rgba(0, 0, 0, 0)',
      borderwidth: 0,
      font: {
        color: isLightMode ? '#000000' : '#ffffff',
        size: plotData.length > 6 ? 9 : 10
      },
      tracegroupgap: 8
    } : {
      orientation: 'v',
      x: 1.05,
      y: 1,
      xanchor: 'left',
      yanchor: 'top',
      bgcolor: 'rgba(0, 0, 0, 0)',
      bordercolor: 'rgba(0, 0, 0, 0)',
      borderwidth: 0,
      font: {
        color: isLightMode ? '#000000' : '#ffffff',
        size: 12
      }
    },
    autosize: true,
    responsive: true
  };

  return (
    <div className="mnav-line-chart" style={{ width: '100%', height: '400px' }}>
      <Plot
        data={plotData}
        layout={layout}
        style={{ width: '100%', height: '100%' }}
        config={{ 
          displayModeBar: false,
          responsive: true,
          useResizeHandler: true
        }}
        useResizeHandler={true}
      />
    </div>
  );
};

export default MNAVLineChart;
